<script setup lang="ts">
import { Play, Square, LoaderCircle } from 'lucide-vue-next'
import { Slider } from '~/components/ui/slider'
import { AirunParticlesBackground, AirunLogoLink } from '~/components/ui/airun'
import { formatTime } from './formatTime'
import type { TtsLanguage } from '~/types'

interface Props {
  text: string
  slug: string
  // Language of the article, not the interface locale.
  language: TtsLanguage
}

const props = defineProps<Props>()
const { locale } = useI18n()
const { isGenerating, resolveAudioUrl } = useTtsService()
const {
  play,
  pause,
  stop,
  isLoading,
  isPlaying,
  isPaused,
  duration,
  currentTime,
  seek
} = useServerTTS()

// Resolved once per article and reused: a second press replays the same file
// instead of asking the service again.
const audioUrl = ref<string | null>(null)

const isBusy = computed(() => isGenerating.value || isLoading.value)
const showProgress = computed(() => isPlaying.value || isPaused.value)

const waveDelays = [0, 0.4, 0.2]

// The resolved URL is kept: audio follows the article's language, not the locale.
watch(locale, stop)

onBeforeUnmount(stop)

const togglePlay = async () => {
  if (isBusy.value) return

  if (isPlaying.value) {
    pause()
    return
  }

  if (!audioUrl.value) {
    // Blocks until the service has the file ready — cached or freshly generated.
    audioUrl.value = await resolveAudioUrl({
      text: props.text,
      slug: props.slug,
      language: props.language
    })
  }

  // play() resumes from pause on its own, so the paused case needs no branch.
  if (audioUrl.value) play(audioUrl.value)
}
</script>

<template>
  <div
    class="group relative flex w-[360px] max-w-full flex-col items-stretch gap-3 overflow-hidden rounded-[18px] px-4 py-3 shadow-sm transition-all duration-300 hover:shadow-md"
    style="
      background: linear-gradient(180deg, #0a0e27 0%, #0d1230 100%);
      box-shadow: 0 16px 40px rgba(31, 79, 255, 0.18);
    "
  >
    <AirunParticlesBackground />

    <div class="relative flex items-center gap-3">
      <div class="flex min-w-0 items-center gap-2">
        <AirunLogoLink />
        <span class="truncate text-sm font-semibold tracking-wide text-white">
          {{ $t('ui.tts_controls.listen_with_airun') }}
        </span>
      </div>

      <div class="ml-auto flex shrink-0 items-center gap-2">
        <!-- Filled at rest too, not just while playing: a ghost play button
             read as unfinished next to the site's always-filled CTAs. -->
        <button
          :disabled="isBusy"
          class="group/btn relative flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px] text-white transition-all duration-200 hover:-translate-y-0.5"
          :class="isBusy ? 'cursor-wait' : ''"
          style="
            background: linear-gradient(135deg, #1f4fff, #3f6cff);
            box-shadow: 0 10px 28px rgba(31, 79, 255, 0.45);
          "
          :title="
            isPlaying
              ? $t('ui.tts_controls.pause_reading')
              : $t('ui.tts_controls.play_reading')
          "
          @click="togglePlay"
        >
          <span v-if="isPlaying" class="flex h-4 items-center gap-[3px]">
            <span
              v-for="(delay, i) in waveDelays"
              :key="i"
              class="airun-wave-bar h-full w-[3px] rounded-full bg-white"
              :style="{ animationDelay: `${delay}s` }"
            />
          </span>
          <Play
            v-else-if="!isBusy"
            class="h-5 w-5 transition-transform group-hover/btn:scale-110"
          />
          <LoaderCircle v-else class="h-5 w-5 animate-spin" />
        </button>

        <button
          v-if="showProgress"
          class="group/btn flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px] text-white transition-all duration-200"
          style="background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.12)"
          :title="$t('ui.tts_controls.stop_reading')"
          @click="stop"
        >
          <Square class="h-4 w-4 transition-transform group-hover/btn:scale-110" />
        </button>
      </div>
    </div>

    <div
      v-if="showProgress"
      class="relative mt-2.5 flex items-center gap-2.5 transition-all duration-300"
    >
      <span class="airun-time w-9 text-right text-xs tabular-nums">
        {{ formatTime(currentTime) }}
      </span>
      <Slider
        :model-value="[currentTime]"
        :max="duration || 1"
        :step="1"
        :min="0"
        class="flex-1 [&_[data-orientation=horizontal]>.absolute]:bg-[#1f4fff] [&_[role=slider]]:h-3.5 [&_[role=slider]]:w-3.5 [&_[role=slider]]:border-[#1f4fff] [&_span[data-orientation=horizontal]]:h-1.5 [&_span[data-orientation=horizontal]]:bg-white/15"
        aria-label-name="Audio progress"
        @update:model-value="(val: number[]) => seek(val[0])"
      />
      <span class="airun-time w-9 text-xs tabular-nums">
        {{ formatTime(duration) }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.airun-wave-bar {
  animation: airun-wave 1.3s ease-in-out infinite;
}

@keyframes airun-wave {
  0%,
  100% {
    transform: scaleY(0.35);
  }
  50% {
    transform: scaleY(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .airun-wave-bar {
    animation: none;
    transform: scaleY(0.6);
  }
}

.airun-time {
  color: rgba(219, 230, 255, 0.7);
}
</style>
